
import React, { useEffect, useState } from "react";

import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Alert,
  Empty,
  Spin,
  message,
} from "antd";

import {
  IdcardOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

// IMPORTANT: Import the actual PDF Document, not the default function.
import { StudentIDCardDocument } from "./StudentIDCardPDF";

const { Title, Text } = Typography;
const { Option } = Select;

const StudentsIDCard = () => {
  const { selectedYear } = useAcademicYear();

  const [gradeClasses, setGradeClasses] = useState([]);
  const [selectedGradeClass, setSelectedGradeClass] = useState(null);
  const [students, setStudents] = useState([]);

  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!selectedYear?.id) {
      setStudents([]);
      setGradeClasses([]);
      setSelectedGradeClass(null);
      return;
    }

    fetchGradeClasses();
  }, [selectedYear?.id]);

  const fetchGradeClasses = async () => {
    try {
      setLoadingClasses(true);
      setError("");

      const response = await api.get("api/users/grade-classes/");
      const data = response.data;

      const results = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

      setGradeClasses(results);
    } catch (err) {
      console.error("Failed to fetch grade classes:", err);

      setError(
        err?.response?.data?.detail ||
          "Unable to load grade classes."
      );
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchStudents = async (gradeClassId) => {
    if (!selectedYear?.id || !gradeClassId) {
      setStudents([]);
      return;
    }

    try {
      setLoadingStudents(true);
      setError("");
      setStudents([]);

      const response = await api.get("/student-identification/", {
        params: {
          academic_year: selectedYear.id,
          grade_class: gradeClassId,
        },
      });

      const data = response.data;

      const results = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

      setStudents(results);

      if (!results.length) {
        messageApi.info(
          "No students were found in this class for the selected academic year."
        );
      }
    } catch (err) {
      console.error("Failed to fetch students:", err);

      setStudents([]);

      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to load students for this class."
      );
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleClassChange = (value) => {
    setSelectedGradeClass(value);
    setStudents([]);
    setError("");

    fetchStudents(value);
  };

  const generateIDCards = async () => {
    if (!selectedYear?.id) {
      messageApi.warning(
        "No active academic year is selected."
      );
      return;
    }

    if (!selectedGradeClass) {
      messageApi.warning("Please select a grade class.");
      return;
    }

    if (!students.length) {
      messageApi.warning(
        "There are no students available for this class."
      );
      return;
    }

    try {
      setGenerating(true);
      setError("");

      const selectedClass = gradeClasses.find(
        (gradeClass) =>
          String(gradeClass.id) === String(selectedGradeClass)
      );

      /*
       * FIX:
       * StudentIDCardDocument returns the actual React PDF document.
       * Do not pass the default StudentIDCardPDF function to pdf().
       */
      const document = (
        <StudentIDCardDocument students={students} />
      );

      const blob = await pdf(document).toBlob();

      if (!blob || blob.size === 0) {
        throw new Error(
          "The generated PDF is empty. Check the PDF document and student image URLs."
        );
      }

      const className =
        selectedClass?.name ||
        selectedClass?.class_name ||
        selectedClass?.grade_name ||
        `Class-${selectedGradeClass}`;

      const safeClassName = String(className)
        .replace(/[^a-z0-9]/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase();

      const yearName = String(
        selectedYear?.name ||
          selectedYear?.academic_year ||
          selectedYear?.year ||
          selectedYear?.id
      ).replace(/[^a-z0-9-]/gi, "-");

      const filename =
        `Zion_Praise_Academy_ID_Cards_${safeClassName}_${yearName}.pdf`;

      saveAs(blob, filename);

      messageApi.success(
        `${students.length} student ID card${
          students.length === 1 ? "" : "s"
        } generated successfully.`
      );
    } catch (err) {
      // Print the real error to the browser console for troubleshooting.
      console.error("Failed to generate student ID cards:", err);

      console.error(
        "PDF error details:",
        err?.message,
        err?.stack
      );

      const errorMessage =
        err?.message ||
        "An unknown error occurred while generating the PDF.";

      messageApi.error(
        `Failed to generate student ID cards: ${errorMessage}`,
        8
      );
    } finally {
      setGenerating(false);
    }
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_, record, index) => index + 1,
    },
    {
      title: "Student",
      dataIndex: "student_name",
      key: "student_name",
      render: (value, record) =>
        value ||
        record?.full_name ||
        record?.name ||
        "Unnamed Student",
    },
    {
      title: "Student ID",
      dataIndex: "card_number",
      key: "card_number",
      render: (value, record) => (
        <Tag color="gold">
          {value ||
            record?.student_id ||
            record?.admission_number ||
            "N/A"}
        </Tag>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (value) => value || "N/A",
    },
    {
      title: "Division",
      dataIndex: "division",
      key: "division",
      render: (value, record) =>
        value ||
        record?.division_name ||
        record?.department ||
        "N/A",
    },
    {
      title: "Academic Year",
      dataIndex: "academic_year_name",
      key: "academic_year_name",
      render: (value, record) => (
        <Tag color="gold">
          {value ||
            record?.academic_year ||
            selectedYear?.name ||
            "N/A"}
        </Tag>
      ),
    },
  ];

  return (
    <>
      {contextHolder}

      <Card
        bordered={false}
        style={{
          borderRadius: 14,
          borderTop: "4px solid #D4AF37",
          boxShadow: "0 4px 20px rgba(17, 17, 17, 0.07)",
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{ width: "100%" }}
        >
          <div>
            <Title
              level={3}
              style={{
                marginBottom: 5,
                color: "#111111",
              }}
            >
              <IdcardOutlined
                style={{
                  color: "#C49A24",
                  marginRight: 9,
                }}
              />
              Zion Praise Academy Student ID Cards
            </Title>

            <Text type="secondary">
              CRM School System — Generate professional black-and-gold
              student identification cards for the selected academic year.
            </Text>
          </div>

          {!selectedYear?.id ? (
            <Alert
              type="warning"
              showIcon
              message="No active academic year selected"
              description="Please select or activate an academic year before generating student identification cards."
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message={`Academic Year: ${
                selectedYear?.name ||
                selectedYear?.academic_year ||
                selectedYear?.year ||
                selectedYear.id
              }`}
              description="Select a grade class to load its students and generate their ID cards."
            />
          )}

          <Space
            wrap
            style={{
              width: "100%",
            }}
          >
            <Select
              placeholder="Select Grade Class"
              value={selectedGradeClass}
              onChange={handleClassChange}
              loading={loadingClasses}
              disabled={!selectedYear?.id || loadingClasses}
              style={{ minWidth: 280 }}
              showSearch
              optionFilterProp="children"
              allowClear
              onClear={() => {
                setSelectedGradeClass(null);
                setStudents([]);
                setError("");
              }}
            >
              {gradeClasses.map((gradeClass) => (
                <Option
                  key={gradeClass.id}
                  value={gradeClass.id}
                >
                  {gradeClass.name ||
                    gradeClass.class_name ||
                    gradeClass.grade_name ||
                    `Class ${gradeClass.id}`}
                </Option>
              ))}
            </Select>

            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                selectedGradeClass &&
                fetchStudents(selectedGradeClass)
              }
              disabled={
                !selectedGradeClass ||
                loadingStudents ||
                generating
              }
            >
              Refresh
            </Button>

            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              loading={generating}
              disabled={
                !selectedYear?.id ||
                !selectedGradeClass ||
                !students.length ||
                loadingStudents
              }
              onClick={generateIDCards}
              style={{
                backgroundColor: "#111111",
                border: "1px solid #D4AF37",
                color: "#FFFFFF",
                fontWeight: 700,
                height: 38,
                borderRadius: 7,
                boxShadow: "0 3px 10px rgba(212, 175, 55, 0.18)",
              }}
            >
              {generating
                ? "Generating PDF..."
                : "Generate ID Cards"}
            </Button>
          </Space>

          {error && (
            <Alert
              type="error"
              showIcon
              message="Unable to complete the request"
              description={error}
            />
          )}

          {loadingStudents ? (
            <div
              style={{
                textAlign: "center",
                padding: 40,
              }}
            >
              <Spin size="large" />
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">
                  Loading student records...
                </Text>
              </div>
            </div>
          ) : (
            <Table
              rowKey={(record, index) =>
                record?.id ??
                record?.card_number ??
                record?.student_id ??
                index
              }
              columns={columns}
              dataSource={students}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              locale={{
                emptyText: (
                  <Empty description="Select a grade class to view students" />
                ),
              }}
              scroll={{ x: true }}
            />
          )}
        </Space>
      </Card>
    </>
  );
};

export default StudentsIDCard;
